// Pure, view-agnostic helpers: turning wire data (persisted session
// messages, compacted-snapshot folds) into ChatMessage bubbles, and the
// discussing-gap wire envelope. No React, no state — unit-testable in
// isolation. Ported from App.tsx + components/mobile/discuss_wire.ts,
// which the rebuild collapses into this single home.

import type { Card, ChatMessage, CompactedMessage, SessionMessage } from './types'

/** First non-empty line of `text`, capped to ~40 chars. Used to auto-title
 *  an untitled thread from the user's first message. CJK and Latin both
 *  count as 1 char — the cap bounds the wire payload + keeps the DB tidy. */
export function deriveThreadTitle(text: string): string {
  const firstLine =
    text.split('\n').map((s) => s.trim()).find((s) => s.length > 0) ?? ''
  const MAX = 40
  return firstLine.length > MAX
    ? firstLine.slice(0, MAX).trimEnd() + '…'
    : firstLine
}

const GAP_OPENER_PREFIX = /^\[discussing-gap c-\w+ — opener\]/
const DISCUSSING_GAP_PREFIX = /^\[discussing-gap c-\w+\]/
const USER_SEPARATOR = '\n\n---\n\nUSER: '

/** True when this user message is a synthetic opener (auto-fired on a
 *  Discuss click). Reload paths skip these so they don't render a bubble. */
export function isGapOpenerWire(text: string): boolean {
  return GAP_OPENER_PREFIX.test(text)
}

/**
 * Recover the user-typed text from a persisted message that was sent
 * through `buildDiscussingGapWire`. Returns:
 *  - `null` for synthetic opener turns (caller skips the bubble)
 *  - the unwrapped text for a regular `[discussing-gap c-X]` envelope
 *  - the original text for messages not wrapped at all
 * Falls back to the original text on a malformed envelope.
 */
export function stripDiscussingGapWire(text: string): string | null {
  if (isGapOpenerWire(text)) return null
  if (!DISCUSSING_GAP_PREFIX.test(text)) return text
  const idx = text.lastIndexOf(USER_SEPARATOR)
  if (idx < 0) return text
  return text.slice(idx + USER_SEPARATOR.length)
}

/**
 * Strip the leading `[t-N]` turn-id marker. `runtime/conversation.rs`
 * prepends `[{id}]\n` to each message's first text block so the agent can
 * see and cite stable turn ids — it is an agent-context artifact, never
 * meant for the user's eyes. Strip it everywhere a message becomes a
 * visible bubble (persisted load AND live stream — the backend ships the
 * marker on both paths).
 */
export function stripTurnMarker(text: string): string {
  return text.replace(/^\[t-\d+\]\n?/, '')
}

/**
 * Build the wire payload for a chat turn pinned to a gap.
 *
 * Opener turn (one per discussing-gap thread, auto-fired on Discuss):
 * full envelope with TITLE / KIND / AMBIGUITY / BLOCKS / BODY so the agent
 * can frame the discussion.
 *
 * Subsequent turns: slim `[discussing-gap c-X]\n\n---\n\nUSER: <text>`.
 * The system prompt keys the discussion focus off the marker alone; the
 * full body is one `read_card` away, so repeating it per turn is pure
 * duplication prefix caching can't recover.
 *
 * The chat bubble shown to the user is always the original text — only
 * the wire payload carries the envelope.
 */
export function buildDiscussingGapWire(
  gap: Card,
  allCards: Card[],
  userText: string,
  options?: { opener?: boolean },
): string {
  const opener = options?.opener === true

  if (!opener) {
    return `[discussing-gap ${gap.card_id}]\n\n---\n\nUSER: ${userText}`
  }

  const proposedKind =
    typeof gap.extra?.['proposed_kind'] === 'string'
      ? (gap.extra['proposed_kind'] as string)
      : 'card'
  const ambiguity =
    typeof gap.extra?.['ambiguity_reason'] === 'string'
      ? (gap.extra['ambiguity_reason'] as string)
      : null
  const blockIds = Array.isArray(gap.extra?.['blocks'])
    ? (gap.extra['blocks'] as unknown[]).filter(
        (x): x is string => typeof x === 'string',
      )
    : []

  // Resolve each block id to (kind, title) so the agent sees the
  // consequence surface — what's actually blocked. Missing ids surface
  // as "(not in cards)" rather than failing silently.
  const blockSummaries = blockIds.map((id) => {
    const c = allCards.find((c) => c.card_id === id)
    if (!c) return `${id}: (not in cards)`
    const title =
      c.title?.trim() ||
      c.body
        .split('\n')
        .find((l) => l.trim().length > 0)
        ?.trim()
        .slice(0, 60) ||
      '(untitled)'
    return `${id} (${c.kind}): ${title}`
  })

  const lines: string[] = [
    `[discussing-gap ${gap.card_id} — opener] — full gap context follows; produce the opener described at the end. The user has NOT typed anything yet.`,
    '',
    `GAP TITLE: ${gap.title?.trim() || '(untitled)'}`,
    `PROPOSED KIND ON APPROVE: ${proposedKind}`,
  ]
  if (ambiguity) lines.push(`AMBIGUITY REASON: ${ambiguity}`)
  if (blockSummaries.length > 0) {
    lines.push('BLOCKS:')
    for (const s of blockSummaries) lines.push(`  - ${s}`)
  }
  if (gap.body.trim()) {
    lines.push('', 'GAP BODY:', gap.body.trimEnd())
  }
  lines.push(
    '',
    '---',
    '',
    'OPENER INSTRUCTION:',
    'The user just clicked Discuss on this gap. Open the conversation in one short message:',
    '  1. Restate what the gap is asking in one sentence.',
    '  2. Survey related cards already in the project — what is defined, what is blank. Cite by [[c-X]].',
    '  3. List 3–5 concrete sub-questions to decide.',
    '  4. End with one open question: where does the user want to start?',
    'Keep it terse (under ~200 words). Do NOT write any cards yet — frame only.',
  )
  return lines.join('\n')
}

/**
 * Flatten persisted session messages into ChatMessage bubbles — the shape
 * the UI renders. Used to hydrate a thread on open and to land observer
 * devices on the canonical post-turn list after `thread_turn_ended`.
 *
 * No per-message timestamps exist in the wire shape, so `ts` is synthesised
 * from now() — order is preserved by array index regardless.
 */
export function flattenSession(msgs: SessionMessage[]): ChatMessage[] {
  const ts = Date.now()
  const flat: ChatMessage[] = []
  for (const m of msgs) {
    const turnId = m.id
    if (m.role === 'user') {
      const raw = stripDiscussingGapWire(
        stripTurnMarker(m.blocks.find((b) => b.type === 'text')?.text ?? ''),
      )
      const text = raw
      if (text && text.trim()) {
        flat.push({ kind: 'user', text, ts, turnId })
      }
    } else if (m.role === 'assistant') {
      // `truncated` is per-message; mark the LAST assistant text bubble
      // emitted for this message so the cut-off badge lands where it cut.
      let lastTextIdx = -1
      for (const b of m.blocks) {
        if (b.type === 'text' && b.text) {
          flat.push({ kind: 'assistant', text: stripTurnMarker(b.text), ts, turnId })
          lastTextIdx = flat.length - 1
        } else if (b.type === 'tool_use') {
          flat.push({
            kind: 'tool_call',
            tool: b.name,
            input: b.input ?? '',
            ts,
            turnId,
            toolUseId: b.id,
          })
        } else if (b.type === 'thinking' && b.text) {
          // Persisted reasoning — `done: true` so it renders collapsed.
          flat.push({ kind: 'thinking', text: b.text, done: true, ts, turnId })
        }
      }
      if (m.truncated && lastTextIdx >= 0) {
        const last = flat[lastTextIdx]
        if (last.kind === 'assistant') {
          flat[lastTextIdx] = { ...last, truncated: true }
        }
      }
    }
    // role 'tool' / 'system' carry no user-visible bubble.
  }
  return flat
}

/**
 * Convert a server `compacted_snapshot` event into the same bubbles the
 * live reducer would have produced from the raw deltas it folds. Used by
 * both the live-send and cold-reattach reducers when events have aged out
 * of the hub's replay buffer.
 *
 * The snapshot loses text↔tool interleaving within one assistant message;
 * we render in canonical order (thinking → tool calls → text).
 *
 * Side effect: seeds `toolNames` for half-folded tool calls (start folded,
 * end still pending) so the live tail's `tool_use_end` can resolve a name.
 */
export function bubblesFromCompactedSnapshot(
  evt: { completed_messages: CompactedMessage[]; in_progress: CompactedMessage | null },
  toolNames: Map<string, string>,
  turnId?: string,
): ChatMessage[] {
  const out: ChatMessage[] = []
  const tag = turnId !== undefined ? { turnId } : {}
  const renderMessage = (m: CompactedMessage) => {
    const ts = Date.now()
    if (m.thinking.length > 0) {
      out.push({ kind: 'thinking', text: m.thinking, done: m.thinking_ended, ts, ...tag })
    }
    for (const tc of m.tool_calls) {
      if (tc.input !== null) {
        out.push({ kind: 'tool_call', tool: tc.name, input: tc.input, ts, toolUseId: tc.id, ...tag })
      } else {
        // Half-folded: ToolUseStart aged out, ToolUseEnd still to come.
        toolNames.set(tc.id, tc.name)
      }
    }
    if (m.text.length > 0) {
      out.push({ kind: 'assistant', text: m.text, ts, ...tag, ...(m.truncated ? { truncated: true } : {}) })
    }
  }
  for (const m of evt.completed_messages) renderMessage(m)
  if (evt.in_progress) renderMessage(evt.in_progress)
  return out
}
